import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/request';
import type { InvestigationAgentContext } from '@onefootprint/request-types/dashboard';
import { LoadingSpinner } from '@onefootprint/ui';
import useAgentDecisionNode from '../../../hooks/use-agent-decision-node';

// Only the emitted value is shown. The artifact also carries the probability behind it for the
// agent's own explain tool, but that is model internals, not something a reviewer acts on.
type DecisionResult = {
  value?: unknown;
};
const parse = (content: string): DecisionResult | null => {
  try {
    const parsed: unknown = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? parsed as DecisionResult : null;
  } catch {
    return null;
  }
};
const formatValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (value === null || value === undefined) return '—';
  return String(value);
};
type DecisionResultViewProps = {
  content: string;
  agent?: InvestigationAgentContext;
  nodeId?: string;
  fieldKey?: string;
};
const DecisionResultView = t0 => {
  const $ = _c(18);
  const {
    content,
    agent,
    nodeId,
    fieldKey
  } = t0;
  const t1 = agent?.id ?? "";
  const t2 = agent?.versionId ?? "";
  const t3 = nodeId ?? "";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = {
      agentId: t1,
      versionId: t2,
      nodeId: t3
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    node,
    isPending,
    error
  } = useAgentDecisionNode(t4);
  let t5;
  let t6;
  if ($[4] !== content || $[5] !== error || $[6] !== fieldKey || $[7] !== isPending || $[8] !== node?.questions) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const result = parse(content);
      const question = node?.questions.find(candidate => candidate.key === fieldKey);
      if (isPending) {
        let t7;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <div className="flex items-center justify-center py-6"><LoadingSpinner /></div>;
          $[11] = t7;
        } else {
          t7 = $[11];
        }
        t6 = t7;
        break bb0;
      }
      let t7;
      if ($[12] !== error) {
        t7 = error && <p className="text-body-3 text-tertiary">Question details unavailable: {getErrorMessage(error)}</p>;
        $[12] = error;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      let t8;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <h3 className="text-label-3 text-tertiary">Value</h3>;
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      const t9 = fieldKey ? `outputs.${fieldKey} = ` : "";
      const t10 = formatValue(result?.value);
      let t11;
      if ($[15] !== t10 || $[16] !== t9) {
        t11 = <section className="flex flex-col gap-1.5">{t8}<p className="text-body-3 text-primary whitespace-pre-wrap break-words">{t9}{t10}</p></section>;
        $[15] = t10;
        $[16] = t9;
        $[17] = t11;
      } else {
        t11 = $[17];
      }
      t5 = <div className="h-full overflow-auto flex flex-col gap-6">{t7}{question && <section className="flex flex-col gap-1.5"><h3 className="text-label-3 text-tertiary">Question</h3><p className="text-body-3 text-primary whitespace-pre-wrap break-words">{question.prompt}</p></section>}{t11}{question?.kind === "pick_one" && <section className="flex flex-col gap-1.5"><h3 className="text-label-3 text-tertiary">Options</h3><ul className="flex flex-col gap-1 m-0 p-0 list-none">{question.options.map(_temp)}</ul></section>}{question?.kind === "score" && <section className="flex flex-col gap-1.5"><h3 className="text-label-3 text-tertiary">Levels</h3><ul className="flex flex-col gap-1 m-0 p-0 list-none">{question.levels.map(_temp2)}</ul></section>}</div>;
    }
    $[4] = content;
    $[5] = error;
    $[6] = fieldKey;
    $[7] = isPending;
    $[8] = node?.questions;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  return t5;
};
export default DecisionResultView;
function _temp(option) {
  return <li key={option.value} className="text-body-3 text-primary break-words"><span className="text-snippet-2">{option.value}</span>{option.description && <span className="text-tertiary"> — {option.description}</span>}</li>;
}
function _temp2(level, index) {
  return <li key={index} className="text-body-3 text-primary break-words"><span className="text-snippet-2">{index}</span><span className="text-tertiary"> — {level}</span></li>;
}